import {
  useWSModal,
  useWSSnackbar,
  WSAlert,
  WSButton,
  WSButtons,
  WSText
} from "@wingspanhq/fe-component-library";
import { IPayerResponse } from "@wingspanhq/payments/dist/interfaces";
import { queriesPayers } from "@query";
import { ClientInfoPanel } from "../../components/ClientInfoPanel";

export interface RestoreClientModalProps {
  client: IPayerResponse;
  onSuccess?: (client: IPayerResponse) => void;
  onClose: () => void;
}

export const RestoreClientModal: React.FC<RestoreClientModalProps> = ({
  client,
  onSuccess,
  onClose
}) => {
  const { openSnackbar } = useWSSnackbar();

  // v3: restore reactivates a deactivated payer (WIN-15189, `restorePayer`),
  // moving it back off the archived (Deactivated) list. The registry auto-
  // invalidates the v3 payer reads and (per its config) the still-v1 payer-row
  // caches the assignment pickers read, so the client reappears as active.
  const [restoreClient, restoreClientMeta] =
    queriesPayers.restorePayer.useMutation();

  const handleRestoreClient = () => {
    restoreClient(
      { payerId: client.payerId },
      {
        onSuccess: () => {
          onSuccess?.(client);
          onClose();
          openSnackbar({
            type: "success",
            duration: 5000,
            message: "Client restored successfully"
          });
        },
        onError: () => {
          openSnackbar({
            type: "warning",
            duration: 5000,
            message: "Failed to restore client"
          });
        }
      }
    );
  };

  return (
    <>
      <WSText.ParagraphSm mb="L" weight="book">
        Are you sure you want to restore this client?
      </WSText.ParagraphSm>

      <ClientInfoPanel client={client} mb="XL" />

      <WSAlert
        mb="XL"
        theme="info"
        icon="info-circle"
        size="M"
        title="Restoring client"
      >
        After a client is restored, invoices can be created for this client
        again and it returns to your active Clients list.
      </WSAlert>

      <WSButtons format="modal">
        <WSButton
          size="M"
          name="restoreClient"
          ml="M"
          loading={restoreClientMeta.isLoading}
          onClick={handleRestoreClient}
        >
          Restore client
        </WSButton>
        <WSButton.Tertiary size="M" onClick={onClose}>
          Cancel
        </WSButton.Tertiary>
      </WSButtons>
    </>
  );
};

export const useRestoreClientModal = () =>
  useWSModal(RestoreClientModal, {
    title: "Restore client",
    size: "M"
  });
